import { useState } from 'react';
import { ChevronLeftIcon, ListChecksIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { TaskPrimaryAction, type TaskChoice } from '@/features/tasks';
import { HeldCountdown } from '@/shared/components/HeldCountdown';
import { choiceLabel } from '../rowChoices';
import { actionFor } from '../heldActions';
import { useHeldActions } from '../heldActionsContext';
import type { QueueRow } from '../yourTurn';
import { WhatItProduced } from './WhatItProduced';

/**
 * Review in a row (spec 154 US3): every decision in Your turn, one at a
 * time, with what it produced already open.
 *
 * - The decisions are taken from the queue when the sheet opens, in its
 *   order. One resolved elsewhere meanwhile is skipped, and said so.
 * - A choice is held like any other (six seconds, Undo) and the sheet moves
 *   on; the last one held stays at the top with its Undo.
 * - It ends on the all-clear, with how many were decided and skipped.
 * - Closing it returns focus to "Review in a row"; held actions keep counting.
 */
export function ReviewInARow({
  workspaceId,
  decisions,
}: {
  workspaceId: string;
  /** The queue's decision rows now, in queue order. */
  decisions: readonly QueueRow[];
}) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        render={
          <Button
            type="button"
            size="xs"
            variant="outline"
            className="pointer-coarse:min-h-11"
          />
        }
      >
        <ListChecksIcon aria-hidden="true" strokeWidth={1.5} />
        Review in a row
      </DialogTrigger>
      <DialogContent
        className="flex max-h-[min(90dvh,44rem)] flex-col overflow-hidden pb-6 sm:max-w-xl"
        // One decision after another: the whole screen on a phone (spec 261).
        phoneLayout="full"
      >
        {open ? (
          <Session
            workspaceId={workspaceId}
            decisions={decisions}
            onClose={() => {
              setOpen(false);
            }}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function Session({
  workspaceId,
  decisions,
  onClose,
}: {
  workspaceId: string;
  decisions: readonly QueueRow[];
  onClose: () => void;
}) {
  const { state, hold, undo } = useHeldActions();
  // Fixed when the sheet opens: the walk does not reorder under the person.
  const [ids] = useState(() => decisions.map((row) => row.taskId));
  const [index, setIndex] = useState(0);
  const [decided, setDecided] = useState<readonly string[]>([]);
  const [skipped, setSkipped] = useState<readonly string[]>([]);
  // The task last held here, whose countdown and Undo stay on top.
  const [lastHeld, setLastHeld] = useState<string | null>(null);

  const byId = new Map(decisions.map((row) => [row.taskId, row]));
  const total = ids.length;
  const done = index >= total;
  const id = ids[index];
  const row = id === undefined ? undefined : byId.get(id);
  // Resolved elsewhere: no longer a decision in the queue, or already held.
  const gone =
    !done && (row === undefined || actionFor(state, row.taskId) !== undefined);
  const shown = lastHeld ? actionFor(state, lastHeld) : undefined;

  const next = () => {
    setIndex((current) => current + 1);
  };

  const choose = (choice: TaskChoice) => {
    if (!row) return;
    const label = choiceLabel(choice, row);
    if (!hold({ choice, rows: [row], label, subject: row.title })) return;
    setDecided((current) => [...current, row.taskId]);
    setLastHeld(row.taskId);
    next();
  };

  return (
    <div className="flex min-h-0 flex-col gap-4">
      <div className="flex items-baseline gap-3 pe-8">
        <DialogTitle>Review in a row</DialogTitle>
        <p className="text-muted-foreground ms-auto text-xs tabular-nums">
          {done
            ? `${String(total)} of ${String(total)}`
            : `${String(index + 1)} of ${String(total)}`}
        </p>
      </div>

      {shown && shown.state === 'held' ? (
        <div className="border-border flex items-center gap-2 rounded-md border px-3 py-2">
          <HeldCountdown
            action={shown}
            title={titleOf(byId, shown.taskIds[0])}
            onUndo={() => {
              const taskId = shown.taskIds[0];
              undo(shown.id);
              setLastHeld(null);
              if (taskId) {
                setDecided((current) =>
                  current.filter((each) => each !== taskId),
                );
                const back = ids.indexOf(taskId);
                if (back >= 0) setIndex(back);
              }
            }}
          />
        </div>
      ) : null}

      <div className="min-h-0 overflow-y-auto">
        {done ? (
          <AllClear
            decided={decided.length}
            skipped={skipped.length}
            onClose={onClose}
          />
        ) : gone || !row ? (
          <div className="flex flex-col gap-3">
            <DialogDescription>
              {row
                ? `${row.title} was decided meanwhile.`
                : 'This decision was resolved elsewhere meanwhile.'}
            </DialogDescription>
            <div>
              <Button type="button" size="sm" onClick={next}>
                Next
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-0.5">
              <p className="text-foreground text-base font-medium">
                {row.title}
              </p>
              <DialogDescription>
                {row.parent
                  ? `${row.parent.title} › `
                  : `${row.workflowName} · `}
                {row.stepLabel ?? 'No step on this board'}
                {' · waiting for your decision'}
              </DialogDescription>
            </div>
            <WhatItProduced
              key={row.taskId}
              workspaceId={workspaceId}
              row={row}
              defaultOpen
            />
            <div className="flex flex-wrap items-center gap-2">
              {row.hasBoard ? (
                <TaskPrimaryAction
                  workspaceId={workspaceId}
                  task={row.task}
                  step={row.step}
                  steps={row.steps}
                  density="header"
                  onChoose={choose}
                />
              ) : null}
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setSkipped((current) =>
                    current.includes(row.taskId)
                      ? current
                      : [...current, row.taskId],
                  );
                  next();
                }}
              >
                Skip
              </Button>
              {index > 0 ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setIndex((current) => Math.max(0, current - 1));
                  }}
                >
                  <ChevronLeftIcon aria-hidden="true" />
                  Previous
                </Button>
              ) : null}
            </div>
          </div>
        )}
      </div>

      {/* How far through, along the sheet's edge. A plain fill: the brand
          gradient is waived for the In motion rings only (WD-07). */}
      <span
        aria-hidden="true"
        className="bg-muted absolute inset-x-0 bottom-0 h-1"
      >
        <span
          className="bg-primary absolute inset-y-0 left-0 transition-[width] motion-safe:duration-[var(--motion-duration-base)]"
          style={{
            width: `${String(total === 0 ? 100 : (Math.min(index, total) / total) * 100)}%`,
          }}
        />
      </span>
    </div>
  );
}

function titleOf(
  byId: ReadonlyMap<string, QueueRow>,
  taskId: string | undefined,
): string {
  return (taskId && byId.get(taskId)?.title) || 'this task';
}

function AllClear({
  decided,
  skipped,
  onClose,
}: {
  decided: number;
  skipped: number;
  onClose: () => void;
}) {
  return (
    <div className="flex flex-col items-start gap-3 py-2">
      <p className="text-foreground text-base font-medium">You are through.</p>
      <DialogDescription>
        {`${String(decided)} decided, ${String(skipped)} skipped.`}
      </DialogDescription>
      <Button type="button" size="sm" onClick={onClose}>
        Back to the Overview
      </Button>
    </div>
  );
}
